import { useEffect, useState } from "react";
import { OBMarketingAnswerModel } from "@models/Models";
import ParticlesBackground from "@components/common/ParticlesBackground";
import { MxResult, OBPersonaAnswerKey } from "@models/Enums";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import { recordEvent } from "@utils/managers/AnalyticsManager";
import MarketingRow from "./MarketingRow";
import { postOnboardingMarketingAnswer } from "@utils/managers/networking/NetworkManager";

const baseMarketingAnswers: OBMarketingAnswerModel[] = [
	{
		emoji: "🥰",
		title: "Referral from a friend",
		key: OBPersonaAnswerKey.referralFromFriend,
	},
	{
		emoji: "🔦",
		title: "Searching Google",
		key: OBPersonaAnswerKey.searchingGoogle,
	},
	{
		emoji: "📱",
		title: "App Store or Google Play Store",
		key: OBPersonaAnswerKey.appStore,
	},
	{
		emoji: "📰",
		title: "From a newsletter",
		key: OBPersonaAnswerKey.newsletter,
	},
];

const Marketing = (props) => {

	const shuffleArray = (array: OBMarketingAnswerModel[]): OBMarketingAnswerModel[] => {
		const newArray = [...array];
		for (let i = newArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
		}
		newArray.push({ emoji: "🫢", title: "Something else", key: OBPersonaAnswerKey.somethingElse });
		return newArray;
	};
	
	const location = useLocation();
	const mxResult: string | undefined = location.state?.mxResult;
	const [selectedAnswer, setSelectedAnswer] = useState<OBMarketingAnswerModel | null>(null);
	const [marketingAnswers] = useState(() => shuffleArray(baseMarketingAnswers));
	const navigate = useNavigate();

	useEffect(() => {
		recordEvent(kAnalyticsConstants.Onboarding.marketingAnswerPresented);
	}, []);

	const onAnswerClick = (answer: OBMarketingAnswerModel) => {
		setSelectedAnswer(answer);
	};

	const goToNextScreen = () => {
		recordEvent(kAnalyticsConstants.Onboarding.marketingAnswerAnswered, { selected_answer: selectedAnswer?.key ?? "" });

		if (selectedAnswer) {
			postOnboardingMarketingAnswer(selectedAnswer.key);
		}

		switch (mxResult) {
			case MxResult.Gmail:
				navigate("/get-started/connect-gmail", { replace: true });
				break;
			case MxResult.Outlook:
				navigate("/get-started/connect-outlook", { replace: true });
				break;
			default:
				navigate("/get-started/meco-mailbox", { replace: true });
				break;
		}
	};

	return (
		<div>
			<ParticlesBackground />
			<div className={`flex min-h-[100dvh] flex-col md:justify-center justify-start py-2 md:py-12 pointer-events-auto`}>
				<div className="fixed top-0 bg-black/50 w-full p-3 z-30 backdrop-blur-lg border-b border-white/10 shadow-2xl">
					<div className="max-w-[1080px] w-full z-30 m-auto">
						<div className="flex justify-end items-center">
							<button className={`text-white font-medium font-primary text-lg focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green/80 hover:bg-success-green/100 transition ease-in-out whitespace-nowrap ${selectedAnswer === null ? "opacity-50 cursor-not-allowed" : ""}`} onClick={goToNextScreen} disabled={selectedAnswer === null}>Next</button>
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-10 items-center justify-center p-3 mt-[50px] md:mt-[80px]">
					<div className="meco_container max-w-[1080px] py-[40px] md:p-[60px] md:pb-[40px]">
						<div className="flex flex-col md:flex-row gap-5 items-start">
							<div className="sticky md:top-[90px] w-full md:w-1/2 flex flex-col gap-2">
								<div className="text-white font-bold font-primary text-4xl leading-10">How did you hear about Meco?</div>
								<div className="text-force-primary-light font-medium font-primary text-base  md:text-xl">We'd love to know how you discovered Meco</div>
							</div>
							<div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-3 max-w-[450px] md:max-w-none m-auto md:min-w-[400px]">
								<div className="text-white font-medium font-primary text-3xl text-center w-full max-w-[600px] mb-5  no-select">
									{marketingAnswers.map((answer, i) => {
										return <MarketingRow marketingAnswer={answer} isSelected={selectedAnswer?.key === answer.key} onSelect={onAnswerClick} />;
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Marketing;
